/**
 * Channels view component.
 * @module components/View/ChannelsView
 */

import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Input,
  Label,
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHeaderCell,
} from 'semantic-ui-react';
import { Link, useHistory } from 'react-router-dom';

import { Icon } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';

import downSVG from '@plone/volto/icons/down-key.svg';
import upSVG from '@plone/volto/icons/up-key.svg';
import clearSVG from '@plone/volto/icons/clear.svg';
import addSVG from '@plone/volto/icons/add.svg';

/**
 * Channels view component class.
 * @function ChannelsView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const ChannelsView = ({ content }) => {
  const history = useHistory();
  let items = [...content.items];

  // Filtering
  const [title, setTitle] = useState('');

  const titleRef = useRef(null);

  const filterField = (items, field, value) =>
    value === ''
      ? items
      : (items = items.filter(
          (item) =>
            item[field] &&
            item[field].toLowerCase().indexOf(value.toLowerCase()) !== -1,
        ));

  items = filterField(items, 'title', title);

  // Sorting
  const [sort, setSort] = useState('title');
  const [dir, setDir] = useState('asc');

  items = items.sort((a, b) =>
    (dir === 'asc' ? a[sort] > b[sort] : a[sort] < b[sort]) ? 1 : -1,
  );

  const onSort = (value) => {
    if (sort === value) {
      setDir(dir === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(value);
      setDir('asc');
    }
  };

  const SortIcon = () => (
    <Icon
      name={dir === 'desc' ? upSVG : downSVG}
      className="icon"
      size="20px"
    />
  );

  return (
    <article id="channels">
      <div className="button-toolbar">
        <Link
          className="circular-button primary float-left"
          to={`${flattenToAppURL(content['@id'])}/add?type=Channel`}
        >
          <Icon name={addSVG} size="24px" />
        </Link>
        <div className="button-toolbar-title">
          Channels <Label circular>{items.length}</Label>
        </div>
      </div>
      <div className="view-padding">
        <div className="scrollable-table">
          <Table striped celled selectable>
            <TableHeader>
              <TableRow className="tools-header">
                <TableHeaderCell onClick={() => onSort('title')} width="6">
                  Title {sort === 'title' && <SortIcon />}
                </TableHeaderCell>
              </TableRow>
              <TableRow className="tools-filter">
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={title}
                      placeholder="Filter by title"
                      ref={titleRef}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                    {title && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setTitle('');
                          titleRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow
                  className="clickable-row"
                  onClick={() => history.push(flattenToAppURL(item['@id']))}
                >
                  <TableCell>{item.title}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </article>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
ChannelsView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
  }).isRequired,
};

export default ChannelsView;
